import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { quickActionsKeys } from "./keys";
import { QuickActionsEncounterQueryOptions, TodayEncountersResponse, TodosResponse } from "../types";
import { buildQueryString } from "~/utils/url";
import { formatEncounterStatusTag, formatEncounterFullTime, formatFullDateAbbreviated } from "@workspace/lib/date";
import { EncounterAppointmentStatus, FormattedEncounter } from "../../../encounters/types";
import { GetAppointmentByIdResponse } from "../types"
// import { getApiProgram } from "~/lib/utils";
import { getRawProgram } from "~/lib/utils";

export const todosQueryOptions = (program?: string) => {
  const rawProgram = getRawProgram(program ?? "")
  return queryOptions({
    queryKey: quickActionsKeys.todos(program),
    retry: false,
    queryFn: async (): Promise<TodosResponse> => {
      try {
        const response = await apiClient(`/admin/todos?program=${program}`);
        const result = await handleResponse<TodosResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },
    select(data) {
      return [
        {
          title: "Today's Encounters",
          amount: data?.todos ?? 0,
          url: `/dashboard/${rawProgram}/todos`,
          initial: true
        },
        {
          title: "Total Consultations",
          amount: data?.total_consultations ?? 0,
          url: `/dashboard/${rawProgram}/encounters/consultation`,
          initial: false
        },
        {
          title: "Total Refill Request",
          amount: data?.total_refills ?? 0,
          url: `/dashboard/${rawProgram}/encounters/refill`,
          initial: false
        },
        {
          title: "Total Live Consultations",
          amount: data?.total_appointments ?? 0,
          url: `/dashboard/${rawProgram}/encounters/appointment`,
          initial: false
        }
      ];
    }
  });
};

export const todayQuickActionsEncountersQueryOptions = (opts: QuickActionsEncounterQueryOptions) => {
  return queryOptions({
    queryKey: quickActionsKeys.todayEncounters(opts),
    retry: true,
    queryFn: async () => {
      try {
        const response = await apiClient(`/admin/encounters${buildQueryString(opts)}`);
        const result = await handleResponse<TodayEncountersResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }

        return result.data;
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },
    select(data) {

      const formattedEncounters = data.encounters?.map(encounter => ({
        id: encounter.type === "appointment" ? encounter.appointment_id : encounter.type === "refill" ? encounter.is_batch_refill ? encounter.refill_batch_id : encounter.refill_id : encounter.type === "prescription" ? encounter.prescription_id : encounter.type === "consultation" ? encounter.encounter_id : encounter.encounter_id ?? undefined,
        type: encounter.type,
        encounterId: encounter.encounter_id,
        title: encounter.type === "appointment" ? `${encounter.patient.name} Appointment` : encounter.type === "refill" ? `${encounter.patient.name} Refill Request` : encounter.type === "prescription" ? `${encounter.patient.name} Prescription` : encounter.type === "consultation" ? `${encounter.patient.name} Consultation` : `${encounter.patient.name}`,
        description: encounter.type === "appointment" ? "Weight Loss Consultation" : "Medical Refill Request",
        createdAt: `${encounter.encounter_datetime ? formatFullDateAbbreviated(encounter.encounter_datetime) : "No Date Available"}`,
        instanceCreated: encounter?.encounter_datetime,
        date: `${encounter.encounter_datetime ? formatFullDateAbbreviated(encounter.encounter_datetime) : "No Date Available"}`,
        time: `${encounter.encounter_datetime ? formatEncounterFullTime(encounter.encounter_datetime) : "No Time Available"}`,
        is_consultation: encounter.is_consultation,
        meetingLocation: "Zoom",
        locationTime: "Local Time",
        session: encounter.session,
        tasks: encounter.recommended_tasks,
        recommendedTasks: encounter.recommended_tasks || [],
        notes: encounter.notes,
        patient: {
          id: encounter.patient.user_id,
          name: encounter.patient.name,
          email: encounter.patient.email,
          gender: encounter.patient.gender ?? "",
          age: encounter.patient.age ?? 0,
          height: encounter.patient.height ?? 0,
          weight: encounter.patient.weight ?? 0,
          target_weight: encounter.patient.target_weight,
          timeframe: encounter.patient.timeframe,
          bmi: encounter.patient.bmi ?? 0,
          user_dosespot_id: encounter.patient.user_dosespot_id ?? "",
          urgent_care_onboarded: encounter.patient.urgent_care_onboarded,
          glp1_guide_accepted: encounter.patient.glp1_guide_accepted,
          identity_verified: encounter.patient.identity_verified.verified,
          medical_plan: {
            program: encounter.patient.medical_plan?.program,
            session_id: encounter.patient.medical_plan?.session_id,
            medication: encounter.patient.medical_plan?.medication ?? {},
            consultation: encounter.patient.medical_plan?.consultation ?? {},
            consultation_type: encounter.patient.medical_plan?.consultation_type ?? "",
            success_url: encounter.patient.medical_plan?.success_url ?? "http://localhost:5174/consultations/confirmation",
            cancel_url: encounter.patient.medical_plan?.cancel_url ?? "http://localhost:5174/consultations/error",
            plan_id: encounter.patient.medical_plan?.plan_id ?? "6850f5f5e9152794ee4e69c0",
            user_id: encounter.patient.medical_plan?.user_id ?? "6850f5cce9152794ee4e69af",
            payment_status: encounter.patient.medical_plan?.payment_status ?? "paid",
            supplementary_questionnaire: encounter.patient.medical_plan?.supplementary_questionnaire ?? {},
            created_datetime: encounter.patient.medical_plan?.created_datetime ?? "",
            created_at: encounter.patient.medical_plan?.created_at ?? "",
            vitamin: encounter.patient.medical_plan?.vitamin ?? {}
          },
          supplementary_health_assessment: encounter.patient?.supplementary_health_assessment ?? {},
          health_profile: encounter.patient?.health_profile,
        },
        medicalRefills: encounter.type === "refill"
          ? encounter.medications && Array.isArray(encounter.medications)
            ? encounter.medications.map((med: any) => ({
              name: med.name,
              dosage: med.dosage,
              quantity: med.quantity,
              duration: med.duration,
              refills: med.refills
            }))
            : encounter.medication
              ? Array.isArray(encounter.medication)
                ? encounter.medication.map((med: any) => ({ name: med.name, dosage: med.dosage }))
                : [{ name: (encounter.medication as any).name, dosage: (encounter.medication as any).dosage }]
              : []
          : [],
        requestedAt: `${encounter.type === ""}`,
        needApproval: encounter.type === "refill" ? true : false,
        tag: `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).tag}`,
        status: `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).status}`,
      }) as FormattedEncounter) ?? []

      // Return the first encounter with totalCount properly attached
      if (formattedEncounters.length === 0) return undefined

      return {
        ...formattedEncounters[0],
        totalCount: data.total
      } as FormattedEncounter & { totalCount: number }
    }
  })
}

export const getQuickActionsAppointmentByIdQueryOptions = (id: string) => {
  return queryOptions({
    queryKey: quickActionsKeys.appointmentById(id),
    retry: false,
    queryFn: async () => {
      try {
        const response = await apiClient(`/admin/appointments/${id}`);

        const result = await handleResponse<GetAppointmentByIdResponse>(response)

        if (!result.success) {
          throw new Error(result.error)
        }

        return result.data
      } catch (error) {
        console.error("Error fetching appointment by ID:", error);
        handleApiError(error);
        throw error;
      }
    },
    select(data) {
      return {
        id: data.appointment.appointment_id,
        title: `${data.appointment.title}`,
        description: "",
        location: data.appointment.location ?? "Zoom",
        patient: {
          name: data.appointment.patient.name,
          email: data.appointment.patient.email,
          age: data.appointment.patient.age,
          gender: data.appointment.patient.gender,
          patient_id: data.appointment.patient.user_id,
        },
        meetingLink: data.appointment.zoom_metadata?.join_url ?? "",
        timezone: data.appointment.zoom_metadata?.timezone ?? "Africa/Lagos",
        meetingCreatedAt: `${formatDate(data.appointment.zoom_metadata?.created_at ?? "", "datetime")}`,
        type: data.appointment.type,
        stats: data.appointment.status,
        tag: `${formatEncounterStatusTag({ date: data.appointment.appointment_datetime ?? "", status: data.appointment.status as EncounterAppointmentStatus, meetingInterval: 30 }).tag}`,
        status: `${formatEncounterStatusTag({ date: data.appointment.appointment_datetime ?? "", status: data.appointment.status as EncounterAppointmentStatus, meetingInterval: 30 }).status}`,
        meetingStartingTime: formatDate(`${data.appointment.appointment_datetime}`, "datetime"),
      };

    }
  })
}


